import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getDocument, updateDocument, reextractDocument, fetchDocumentFile } from 'services/documents.service'
import StatusBadge from 'components/StatusBadge'
import { DOC_TYPES } from 'config'
import { useI18n, apiError } from 'i18n'

// Starting shape per doc type, for documents entered by hand (no AI key / failed).
const TEMPLATES = {
  commercial_invoice: { header: { invoiceNumber: '', invoiceDate: '', currency: 'USD', incoterm: '' }, items: [{ sku: '', description: '', quantity: 0, unitValue: 0, amount: 0 }] },
  packing_list: { items: [{ sku: '', description: '', grossWeightKg: 0, volumeCbm: 0 }] },
  pedimento: { header: { pedimentoNumber: '', exchangeRate: 0, customsValue: 0 }, contributions: [{ code: 'IGI', amount: 0 }, { code: 'DTA', amount: 0 }, { code: 'IVA', amount: 0 }], partidas: [] },
  cost: { header: { issuerName: '', documentNumber: '', currency: 'MXN' }, lines: [{ conceptRaw: '', amount: 0, vatAmount: 0 }] }
}
const templateFor = (docType) => TEMPLATES[docType] || TEMPLATES.cost

const DocumentDetail = () => {
  const { t, fmt } = useI18n()
  const { documentId } = useParams()
  const [doc, setDoc] = useState(null)
  const [docType, setDocType] = useState('')
  const [json, setJson] = useState('')
  const [fileUrl, setFileUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const apply = (d) => {
    setDoc(d)
    setDocType(d.docType || '')
    setJson(d.extracted ? JSON.stringify(d.extracted, null, 2) : '')
  }

  const load = useCallback(async () => {
    try {
      apply((await getDocument(documentId)).document)
    } catch (error) {
      toast.error(apiError(error, t('document.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [documentId])

  useEffect(() => { load() }, [load])

  // The original file as a blob (auth header handled by the interceptor), typed
  // with the stored MIME so PDFs and images render.
  const mimeType = doc?.mimeType
  useEffect(() => {
    if (!mimeType) return undefined
    let url
    ;(async () => {
      try {
        const blob = await fetchDocumentFile(documentId)
        url = URL.createObjectURL(new Blob([blob], { type: mimeType }))
        setFileUrl(url)
      } catch { setFileUrl(null) }
    })()
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [documentId, mimeType])

  const onSave = async () => {
    let data
    try {
      data = json.trim() ? JSON.parse(json) : null
    } catch (error) {
      toast.error(t('document.invalidJson', { error: error.message }))
      return
    }
    setBusy(true)
    try {
      apply((await updateDocument(documentId, { docType: docType || undefined, data })).document)
      toast.success(t('document.saved'))
    } catch (error) {
      toast.error(apiError(error, t('document.saveFailed')))
    } finally {
      setBusy(false)
    }
  }

  const onReextract = async () => {
    setBusy(true)
    try {
      apply((await reextractDocument(documentId, docType || undefined)).document)
      toast.success(t('document.extracted_ok'))
    } catch (error) {
      toast.error(apiError(error, t('document.extractFailed')))
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <p className='text-slate-400'>{t('common.loading')}</p>
  if (!doc) return <p className='text-slate-400'>{t('document.notFound')}</p>

  const isImage = doc.mimeType?.startsWith('image/')
  const isPdf = doc.mimeType === 'application/pdf'

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div>
          <Link to={`/shipments/${doc.shipmentId}`} className='text-sm text-slate-500 hover:underline'>{t('document.back')}</Link>
          <div className='mt-1 flex flex-wrap items-center gap-3'>
            <h1 className='text-2xl font-bold'>{doc.fileName}</h1>
            <StatusBadge status={doc.status} />
          </div>
          <p className='mt-1 text-sm text-slate-500'>
            {doc.docType ? t(`docTypes.${doc.docType}`) : t('document.unclassified')} ({fmt.pct(doc.classificationConfidence)}) ·{' '}
            {doc.extractionMethod ? t(`methods.${doc.extractionMethod}`) : '—'} · {t('document.extractionConfidence', { value: fmt.pct(doc.extractionConfidence) })}
          </p>
          {doc.error && <p className='mt-1 text-sm text-status-incomplete'>{doc.error}</p>}
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <select value={docType} onChange={(e) => setDocType(e.target.value)}
            className='rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none'>
            <option value=''>{t('document.typePlaceholder')}</option>
            {DOC_TYPES.map((d) => <option key={d} value={d}>{t(`docTypes.${d}`)}</option>)}
          </select>
          <button onClick={onReextract} disabled={busy} className='rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50'>
            {busy ? t('document.working') : t('document.reextract')}
          </button>
          <button onClick={onSave} disabled={busy} className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
            {t('common.save')}
          </button>
        </div>
      </div>

      <div className='grid gap-6 lg:grid-cols-2'>
        <div className='min-h-[600px] overflow-hidden rounded-xl border border-slate-200 bg-white'>
          {!fileUrl && <p className='p-6 text-slate-400'>{t('document.loadingFile')}</p>}
          {fileUrl && isPdf && <iframe title={doc.fileName} src={fileUrl} className='h-[75vh] w-full' />}
          {fileUrl && isImage && <img alt={doc.fileName} src={fileUrl} className='w-full' />}
          {fileUrl && !isPdf && !isImage && (
            <p className='p-6 text-sm text-slate-500'>{t('document.noPreview', { type: doc.mimeType })} <a href={fileUrl} download={doc.fileName} className='font-semibold text-brand'>{t('document.download')}</a></p>
          )}
        </div>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center justify-between'>
            <h2 className='text-sm font-semibold text-slate-700'>{t('document.extracted')}</h2>
            {!json && (
              <button onClick={() => setJson(JSON.stringify(templateFor(docType), null, 2))} disabled={!docType}
                className='text-sm font-semibold text-brand hover:underline disabled:opacity-40'>
                {t('document.fromTemplate')}
              </button>
            )}
          </div>
          <textarea value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false}
            placeholder={docType ? t('document.emptyWithType') : t('document.emptyNoType')}
            className='h-[75vh] w-full rounded-xl border border-slate-300 bg-white p-3 font-mono text-xs focus:border-brand focus:outline-none' />
          <p className='text-xs text-slate-500'>{t('document.saveHelp')}</p>
        </div>
      </div>
    </div>
  )
}

export default DocumentDetail
